import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import './Registration.css';

const Registration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get tournament ID from URL params and tournament data from state
  const urlParams = new URLSearchParams(location.search);
  const tournamentIdFromURL = urlParams.get('tournamentId');
  const tournamentFromState = location.state?.selectedTournament;
  const tournamentIdFromState = location.state?.tournamentId || location.state?.selectedTournament?.tournamentId;

  // Determine the tournament ID to use (prioritize state, then URL)
  const finalTournamentId = tournamentIdFromState || tournamentIdFromURL;

  // Additional validation to ensure we have a valid tournament ID
  if (!finalTournamentId && tournamentFromState?.tournamentId) {
    console.warn("Using tournamentId from tournament object as backup");
  }

  console.log("Tournament ID Sources:");
  console.log("- From URL:", tournamentIdFromURL);
  console.log("- From State:", tournamentIdFromState);
  console.log("- From Tournament Object:", location.state?.selectedTournament?.tournamentId);
  console.log("- Final ID to use:", finalTournamentId);
  console.log("- Tournament from State:", tournamentFromState);

  const [formData, setFormData] = useState({
    tournamentId: finalTournamentId || "",
    teamName: "",
    teamId: "", // Added teamId
    role: "Player",
    name: "",
    birthdate: "",
    phoneNumber: "",
    state: "",
    city: "",
    email: "",
    photo: null,
    selectedTournamentId: finalTournamentId || "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [teams, setTeams] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(tournamentFromState || null);
  const [submitting, setSubmitting] = useState(false);
  const [userProfilePic, setUserProfilePic] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      const token = localStorage.getItem("token");
      console.log("Token in registration:", token);

      if (!token) {
        console.log("No token found, redirecting to login");
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      // Validate tournament ID
      if (!finalTournamentId) {
        setError("No tournament ID provided. Please select a tournament from the tournaments page.");
        setLoading(false);
        return;
      }

      try {
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        console.log('Fetching data from APIs...');
        console.log('Using tournament ID:', finalTournamentId);

        // If we don't have tournament data from state, fetch it from API
        if (!selectedTournament && finalTournamentId) {
          await fetchTournamentData(headers, finalTournamentId);
        }

        // Get the current user's ID and fetch user data
        const userId = await getCurrentUserId(headers);
        if (userId) {
          setCurrentUserId(userId);
          await fetchCurrentUserDataById(headers, userId);
        }

        // Fetch teams using the new API endpoint
        await fetchTournamentTeams(headers, finalTournamentId);

      } catch (err) {
        console.error("Error fetching initial data:", err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [finalTournamentId, navigate, location]);


  const getCurrentUserId = async (headers) => {
    try {
      console.log('Getting current user ID...');

      // Method 1: Try to decode token to get user ID 
      const token = localStorage.getItem("token");
      if (token) {
        try {
          // Decode JWT token (basic decode - you might need a JWT library for full decode)
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));

          const decodedToken = JSON.parse(jsonPayload);
          console.log('Decoded token:', decodedToken);

          // Common JWT fields that might contain user ID
          const possibleUserIdFields = [
            'sub', 'userId', 'user_id', 'id', 'nameid', 'unique_name',
            'nameidentifier', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
          ];

          for (const field of possibleUserIdFields) {
            if (decodedToken[field]) {
              console.log(`Found user ID in token field '${field}':`, decodedToken[field]);
              return decodedToken[field];
            }
          }
        } catch (tokenError) {
          console.log('Could not decode token:', tokenError);
        }
      }

      // Method 2: If token doesn't contain user ID, get it from the User API list
      console.log('Fetching user list to find current user...');
      const userResponse = await fetch(
        "https://api.asterickx.com/api/User",
        { headers }
      );

      if (userResponse.status === 401) {
        console.log("Token expired, redirecting to login");
        localStorage.removeItem("token");
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return null;
      }

      if (!userResponse.ok) {
        throw new Error(`User API failed with status: ${userResponse.status}`);
      }

      const userData = await userResponse.json();
      console.log('User list response:', userData);

      // If response is an array, get the first user 
      if (Array.isArray(userData) && userData.length > 0) {
        const currentUser = userData[0];
        console.log('Using first user from list, ID:', currentUser.id);
        return currentUser.id;
      } else if (userData && userData.id) {
        console.log('Single user response, ID:', userData.id);
        return userData.id;
      }

      throw new Error('Could not determine current user ID');
    } catch (error) {
      console.error('Error getting current user ID:', error);
      throw error;
    }
  };

  // Function to fetch user data by specific ID
  const fetchCurrentUserDataById = async (headers, userId) => {
    try {
      console.log(`Fetching user data for ID: ${userId}`);

      const userResponse = await fetch(
        `https://api.asterickx.com/api/User/${userId}`,
        { headers }
      );

      if (userResponse.status === 401) {
        console.log("Token expired, redirecting to login");
        localStorage.removeItem("token");
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      if (!userResponse.ok) {
        throw new Error(`User API failed with status: ${userResponse.status}`);
      }

      const userData = await userResponse.json();
      console.log('User data response:', userData);

      if (userData && typeof userData === 'object') {
        // Handle profile picture
        const profilePicField = userData.profilePicture ||
          userData.photo ||
          userData.avatar ||
          userData.image ||
          userData.profileImage ||
          userData.picture;

        if (profilePicField) {
          console.log('Found profile picture:', profilePicField);
          setUserProfilePic(profilePicField);
        }

        // Format birthdate properly
        let formattedBirthdate = "";
        const birthdateField = userData.birthDate ||
          userData.dateOfBirth ||
          userData.dob ||
          userData.birthdate ||
          userData.birth_date ||
          userData.date_of_birth;

        if (birthdateField) {
          try {
            const date = new Date(birthdateField);
            if (!isNaN(date.getTime())) {
              formattedBirthdate = date.toISOString().split('T')[0];
            }
          } catch (dateError) {
            console.warn('Error formatting birthdate:', dateError);
          }
        }

        // Extract user information
        const extractedName = userData.name ||
          userData.firstName ||
          userData.fullName ||
          userData.username ||
          userData.first_name ||
          userData.full_name ||
          (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : "") ||
          "";

        const extractedEmail = userData.email ||
          userData.emailAddress ||
          userData.emailId ||
          "";

        const extractedPhone = userData.phoneNumber ||
          userData.phone ||
          userData.mobile ||
          userData.contactNumber ||
          "";

        const extractedState = userData.state ||
          userData.stateProvince ||
          userData.region ||
          "";

        const extractedCity = userData.city ||
          userData.cityName ||
          userData.location ||
          "";

        // Update form data with current user's information
        setFormData(prev => ({
          ...prev,
          name: extractedName,
          email: extractedEmail,
          phoneNumber: extractedPhone,
          state: extractedState,
          city: extractedCity,
          birthdate: formattedBirthdate,
        }));

        console.log('User data loaded and form updated successfully');
      }
    } catch (userError) {
      console.error('Error fetching user data:', userError);

      if (userError.message.includes('401')) {
        localStorage.removeItem("token");
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      console.log('Error fetching user data, user will need to fill form manually');
    }
  };

  const fetchTournamentData = async (headers, tournamentId) => {
    try {
      console.log('Fetching tournament data for ID:', tournamentId);

      const tournamentResponse = await fetch(
        "https://api.asterickx.com/api/Tournament",
        { headers }
      );

      if (tournamentResponse.status === 401) {
        console.log("Token expired, redirecting to login");
        localStorage.removeItem("token");
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      if (!tournamentResponse.ok) {
        throw new Error(`Tournament API failed: ${tournamentResponse.status}`);
      }

      const tournamentsData = await tournamentResponse.json();
      console.log('All tournaments from API:', tournamentsData);

      if (!Array.isArray(tournamentsData) || tournamentsData.length === 0) {
        setError("No tournaments available. Please contact administrator.");
        return;
      }

      // Find the specific tournament by ID
      const foundTournament = tournamentsData.find(t =>
        t && t.id && t.id.toString() === tournamentId.toString()
      );

      console.log('Looking for tournament ID:', tournamentId);
      console.log('Found tournament:', foundTournament);

      if (foundTournament) {
        setSelectedTournament(foundTournament);

        setFormData(prev => ({
          ...prev,
          selectedTournamentId: foundTournament.id.toString(),
          tournamentId: foundTournament.id.toString()
        }));

        console.log('Tournament data set successfully:', foundTournament);
      } else {
        console.warn('Tournament ID from URL not found in available tournaments');
        setError(`Tournament with ID ${tournamentId} is not available or you don't have access to it.`);
      }
    } catch (err) {
      console.error("Error fetching tournament data:", err);
      setError(`Failed to load tournament data: ${err.message}`);
    }
  };

  // NEW: Function to fetch teams by tournament ID using the specific API endpoint
  const fetchTournamentTeams = async (headers, tournamentId) => {
    try {
      setLoadingTeams(true);
      console.log('=== FETCHING TEAMS DEBUG ===');
      console.log('Tournament ID:', tournamentId);
      console.log('Tournament ID type:', typeof tournamentId);
      console.log('API URL:', `https://api.asterickx.com/api/Team/Get-tournament-by-ID/${tournamentId}`);
      console.log('Headers:', headers);

      const teamsResponse = await fetch(
        `https://api.asterickx.com/api/Team/Get-tournament-by-ID/${tournamentId}`,
        {
          method: 'GET',
          headers
        }
      );

      console.log('Teams API Response Status:', teamsResponse.status);
      console.log('Teams API Response Headers:', Object.fromEntries(teamsResponse.headers.entries()));

      if (teamsResponse.status === 401) {
        console.log("Token expired, redirecting to login");
        localStorage.removeItem("token");
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      // Get response text first to see raw response
      const responseText = await teamsResponse.text();
      console.log('Raw API Response:', responseText);

      if (teamsResponse.ok) {
        let teamsData;
        try {
          teamsData = JSON.parse(responseText);
        } catch (parseError) {
          console.error('Failed to parse JSON response:', parseError);
          console.log('Response was not valid JSON:', responseText);
          setTeams([]);
          return;
        }

        console.log('Parsed teams data:', teamsData);
        console.log('Teams data type:', typeof teamsData);
        console.log('Is teams data array?', Array.isArray(teamsData));

        // Handle different possible response structures
        let finalTeams = [];

        if (Array.isArray(teamsData)) {
          finalTeams = teamsData;
          console.log('Teams data is array with', teamsData.length, 'items');
        } else if (teamsData && typeof teamsData === 'object') {
          // Check for common property names that might contain the teams array
          const possibleTeamsProperties = [
            'teams', 'data', 'items', 'results', 'teamList', 'tournamentTeams'
          ];

          for (const prop of possibleTeamsProperties) {
            if (teamsData[prop] && Array.isArray(teamsData[prop])) {
              finalTeams = teamsData[prop];
              console.log(`Found teams array in property '${prop}' with ${finalTeams.length} items`);
              break;
            }
          }

          // If no array found in nested properties, check if it's a single team object
          if (finalTeams.length === 0 && teamsData.id) {
            finalTeams = [teamsData];
            console.log('Single team object found, converting to array');
          }

          console.log('Object properties:', Object.keys(teamsData));
        }

        console.log('Final teams to set:', finalTeams);
        console.log('Number of teams:', finalTeams.length);

        if (finalTeams.length > 0) {
          // Log first team structure for debugging
          console.log('Sample team structure:', finalTeams[0]);
          setTeams(finalTeams);
          console.log('✅ Teams loaded successfully:', finalTeams.length, 'teams found');
        } else {
          console.warn('❌ No teams found for tournament ID:', tournamentId);
          setTeams([]);

          // Try the fallback API as well
          console.log('Trying fallback: fetching all teams and filtering...');
          await fetchAllTeamsAndFilter(headers, tournamentId);
        }
      } else {
        console.error('❌ Teams API failed, status:', teamsResponse.status);
        console.error('Error response:', responseText);

        // Try the fallback API
        console.log('Trying fallback: fetching all teams and filtering...');
        await fetchAllTeamsAndFilter(headers, tournamentId);
      }
    } catch (teamsError) {
      console.error('❌ Teams API error:', teamsError);
      console.log('Trying fallback: fetching all teams and filtering...');
      await fetchAllTeamsAndFilter(headers, tournamentId);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Fallback function to fetch all teams and filter by tournament ID
  const fetchAllTeamsAndFilter = async (headers, tournamentId) => {
    try {
      console.log('=== FALLBACK: FETCHING ALL TEAMS ===');

      const allTeamsResponse = await fetch(
        "https://api.asterickx.com/api/Team",
        { headers }
      );

      if (allTeamsResponse.ok) {
        const allTeamsData = await allTeamsResponse.json();
        console.log('All teams data:', allTeamsData);

        if (Array.isArray(allTeamsData) && allTeamsData.length > 0) {
          // Filter teams by tournament ID
          const filteredTeams = allTeamsData.filter(team => {
            const teamTournamentId = team.tournamentId || team.tournament_id || team.TournamentId;
            console.log(`Team: ${team.teamName || team.name}, Tournament ID: ${teamTournamentId}, Target: ${tournamentId}`);
            return teamTournamentId && teamTournamentId.toString() === tournamentId.toString();
          });

          console.log('Filtered teams:', filteredTeams);
          setTeams(filteredTeams);

          if (filteredTeams.length > 0) {
            console.log('✅ Fallback successful:', filteredTeams.length, 'teams found');
          } else {
            console.warn('❌ No teams found for tournament ID after filtering');
          }
        }
      } else {
        console.error('Fallback API also failed:', allTeamsResponse.status);
        setTeams([]);
      }
    } catch (fallbackError) {
      console.error('Fallback API error:', fallbackError);
      setTeams([]);
    }
  };

  // Function to handle team selection - UPDATED to capture team ID
  const handleTeamSelection = (event) => {
    const selectedTeamName = event.target.value;
    console.log('Team selected:', selectedTeamName);

    // Find the selected team object to get its ID
    const selectedTeam = teams.find(team =>
      (team.teamName || team.name) === selectedTeamName
    );

    console.log('Selected team object:', selectedTeam);

    setFormData(prev => ({
      ...prev,
      teamName: selectedTeamName,
      teamId: selectedTeam ? (selectedTeam.id || selectedTeam.teamId || "").toString() : ""
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      photo: e.target.files[0],
    }));
  };

  const handleRegisterClick = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem("token");

      if (!token) {
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      // Validate required fields - UPDATED validation
      const requiredFields = ['selectedTournamentId', 'teamName', 'teamId'];
      const missingFields = requiredFields.filter(field => !formData[field]?.toString().trim());

      if (missingFields.length > 0) {
        alert(`Please fill in all required fields: ${missingFields.join(', ').replace('selectedTournamentId', 'tournament').replace('teamId', 'team')}`);
        return;
      }

      // Validate that we have a valid user ID
      if (!currentUserId) {
        alert('User information not found. Please refresh the page and try again.');
        return;
      }

      // Prepare form data for submission - UPDATED to match API structure
      const submitData = {
        userId: currentUserId.toString(),
        tournamentId: formData.selectedTournamentId.toString(),
        teamId: formData.teamId.toString()
      };

      console.log("Submitting registration data:", submitData);

      // Submit to the API
      const response = await axios.post(
        "https://api.asterickx.com/api/PlayerRegistrations",
        submitData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Registration API Response:", response.data);
      alert("Registration successful! You have been registered for the tournament.");
      navigate('/tournament');

    } catch (error) {
      console.error("Error submitting registration:", error);

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "There was an error submitting your registration. Please try again.";
      alert(`Registration failed: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <h2>Loading registration form...</h2>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Fetching tournament information and your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Error</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/tournament')}>
            Back to Tournaments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4" style={{ marginBottom: "10rem" }}>
      <header className="text-center mb-4">
        <h1>Tournament Registration</h1>
        {selectedTournament && (
          <div className="alert alert-info mt-3">
            <h5 className="mb-1">Registering for: {selectedTournament.tournamentName}</h5>
            <small>Tournament ID: {selectedTournament.tournamentId}</small>
          </div>
        )}
      </header>

      <div className="row justify-content-center">
        <div className="col-lg-8">
          <form className="p-4 border rounded bg-light shadow">
            {/* Hidden Tournament ID Field */}
            <input
              type="hidden"
              name="tournamentId"
              value={formData.tournamentId}
            />
            <input
              type="hidden"
              name="selectedTournamentId"
              value={formData.selectedTournamentId}
            />
            <input
              type="hidden"
              name="teamId"
              value={formData.teamId}
            />

            {/* Team Name */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="teamName" className="form-label">
                  Team Name <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                  <select
                    className="form-select"
                    id="teamName"
                    name="teamName"
                    value={formData.teamName}
                    onChange={handleTeamSelection}
                    required
                    disabled={loadingTeams}
                  >
                    <option value="">
                      {loadingTeams ? "Loading teams..." : "Select Team"}
                    </option>
                    {teams.map((team, index) => (
                      <option
                        key={team.id || team.teamId || index}
                        value={team.teamName || team.name}
                      >
                        {team.teamName || team.name}
                      </option>
                    ))}
                  </select>
                  {loadingTeams && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>
                {teams.length === 0 && !loadingTeams && (
                  <small className="text-muted">No teams available for this tournament</small>
                )}
                {formData.teamId && (
                  <small className="text-muted">Team ID: {formData.teamId}</small>
                )}
              </div>
            </div>

            {/* Role and Name */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="role" className="form-label">
                  Role
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="role"
                  name="role"
                  value="Player"
                  readOnly
                // style={{ backgroundColor: '#e9ecef' }}
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="name" className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  readOnly
                />
              </div>
            </div>

            {/* Birthdate and Phone Number */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="birthdate" className="form-label">
                  Birthdate
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="birthdate"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleChange}
                  readOnly
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="phoneNumber" className="form-label">
                  Phone Number <span className="text-danger">*</span>
                </label>
                <input
                  type="tel"
                  className="form-control"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  required
                  readOnly
                />
              </div>
            </div>

            {/* City and State */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="city" className="form-label">
                  City
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter your city"
                  readOnly
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="state" className="form-label">
                  State
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="Enter your state"
                  readOnly
                />
              </div>
            </div>

            {/* Email */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="email" className="form-label">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  readOnly
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <button
                type="button"
                className="btn btn-primary btn-lg px-4"
                onClick={handleRegisterClick}
                disabled={submitting || !formData.selectedTournamentId || !formData.teamId}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Registering...
                  </>
                ) : (
                  "Register"
                )}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-lg px-4"
                onClick={() => navigate('/tournament')}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default Registration;